import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import RoleBasedRoute from './components/RoleBasedRoute';
import Layout from './components/Layout';
import InstructorLayout from './components/InstructorLayout';
import Users from './components/pages/Users';
import Courses from './components/pages/Courses';
import Topics from './components/pages/Topics';
import Lessons from './components/pages/Lessons';
import Classes from './components/pages/Classes';
import ClassDetails from './components/pages/ClassDetails';
import Exams from './components/pages/Exams';
import Quizzes from './components/pages/Quizzes';
import Questions from './components/pages/Questions';
import Articles from './components/pages/Articles';
import ClassAttendance from './components/pages/ClassAttendance';
import TestPage from './components/pages/TestPage';
import ClassFeedback from './components/pages/ClassFeedback';
import ClassSharedFiles from './components/pages/ClassSharedFiles';
import ClassAssignment from './components/pages/ClassAssignment';
import Instructor from './components/pages/Instructor';
import AdminManagement from './components/pages/AdminManagement';
import FileManagement from './components/pages/FileManagement';
import InstructorFiles from './components/pages/InstructorFiles';
import WebConfig from './components/pages/WebConfig';
import SessionProvider from './components/SessionProvider';
import SessionManager from './utils/sessionManager';
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on app load
    const storedToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const savedUser = localStorage.getItem('adminUser') || sessionStorage.getItem('adminUser');

    if (storedToken && savedUser && !SessionManager.isSessionExpired()) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        SessionManager.clearSession();
      }
    } else {
      SessionManager.clearSession();
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('icer_admin_user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    SessionManager.clearSession();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-500 to-purple-600">
        <div className="text-white text-xl font-medium">Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <SessionProvider onSessionExpired={handleLogout}>
        <div className="h-screen font-sans antialiased">
          <Routes>
          <Route 
            path="/login" 
            element={
              user ? (
                user.role === 'instructor' ? 
                  <Navigate to="/instructor" replace /> : 
                  <Navigate to="/dashboard" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            } 
          />
          <Route 
            path="/dashboard/*" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Dashboard user={user} onLogout={handleLogout} />
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/users" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <Users />
                </Layout>
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/courses" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <Courses />
                </Layout>
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/topics" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <Topics />
                </Layout>
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/quizzes" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <Quizzes />
                </Layout>
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/lessons" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <Lessons />
                </Layout>
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/classes" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <Classes />
                </Layout>
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/classes/:classId" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin', 'instructor']}>
                <Layout user={user} onLogout={handleLogout}>
                  <ClassDetails />
                </Layout>
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/classes/:classId/attendance" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin', 'instructor']}>
                {user?.role === 'instructor' ? (
                  <InstructorLayout user={user} onLogout={handleLogout}>
                    <ClassAttendance />
                  </InstructorLayout>
                ) : (
                  <Layout user={user} onLogout={handleLogout}>
                    <ClassAttendance />
                  </Layout>
                )}
              </RoleBasedRoute>
            } 
          />
            <Route 
            path="/classes/:classId/feedback" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin', 'instructor']}>
                {user?.role === 'instructor' ? (
                  <InstructorLayout user={user} onLogout={handleLogout}>
                    <ClassFeedback />
                  </InstructorLayout>
                ) : (
                  <Layout user={user} onLogout={handleLogout}>
                    <ClassFeedback />
                  </Layout>
                )}
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/classes/:classId/shared-files" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin', 'instructor']}>
                {user?.role === 'instructor' ? (
                  <InstructorLayout user={user} onLogout={handleLogout}>
                    <ClassSharedFiles />
                  </InstructorLayout>
                ) : (
                  <Layout user={user} onLogout={handleLogout}>
                    <ClassSharedFiles />
                  </Layout>
                )}
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/classes/:classId/assignments" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin', 'instructor']}>
                {user?.role === 'instructor' ? (
                  <InstructorLayout user={user} onLogout={handleLogout}>
                    <ClassAssignment />
                  </InstructorLayout>
                ) : (
                  <Layout user={user} onLogout={handleLogout}>
                    <ClassAssignment />
                  </Layout>
                )}
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/exams" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <Exams />
                </Layout>
              </RoleBasedRoute>
            } 
          />
          <Route
            path="/questions" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <Questions />
                </Layout>
              </RoleBasedRoute>
            }
          />
          <Route
            path="/articles" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <Articles />
                </Layout>
              </RoleBasedRoute>
            }
          />
          <Route
            path="/test" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <TestPage />
                </Layout>
              </RoleBasedRoute>
            }
          />
          <Route
            path="/admin-management" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <AdminManagement />
                </Layout>
              </RoleBasedRoute>
            }
          />
          <Route
            path="/files" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin', 'instructor']}>
                {user?.role === 'instructor' ? (
                  <InstructorLayout user={user} onLogout={handleLogout}>
                    <InstructorFiles key={user.id} user={user} />
                  </InstructorLayout>
                ) : (
                  <Layout user={user} onLogout={handleLogout}>
                    <FileManagement user={user} />
                  </Layout>
                )}
              </RoleBasedRoute>
            }
          />
          <Route
            path="/webconfig" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['admin', 'super_admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <WebConfig />
                </Layout>
              </RoleBasedRoute>
            }
          />
          <Route
            path="/instructor" 
            element={
              <RoleBasedRoute user={user} allowedRoles={['instructor']}>
                <InstructorLayout user={user} onLogout={handleLogout}>
                  <Instructor />
                </InstructorLayout>
              </RoleBasedRoute>
            }
          />
          <Route 
            path="/" 
            element={
              <Navigate to={
                user ? (
                  user.role === 'instructor' ? "/instructor" : "/dashboard"
                ) : "/login"
              } replace />
            }
          />
          </Routes>
        </div>
      </SessionProvider>
    </Router>
  );
}

export default App;
